import { useShallow } from 'zustand/react/shallow';
import { useSuggestedProducts } from '../../../../../services/modules/products/products.queries';
import useOrderBookStore from '../../../../../stores/OrderBook/orderBook.store';
import { useApplicationContext } from '../../../../context/ApplicationContext/ApplicationContext';
import { StatusType } from '../../../../orders/order.types';
import { saleItemIsKit, saleItemIsProductColor } from '../../../utils/saleItemType.utils';
import { ESProductCatalogSearch } from '../../product-catalog-es.types';
import {
  CatalogRowCardsAccess,
  PagesById,
  UseCatalogAccessSuggestionsParams,
} from '../catalogProductsListV2.types';

interface PageEntityIds {
  productIds: string[];
  catalogEntityIds: string[];
}

const getPageEntityIds = (pagesById: PagesById, page: number): PageEntityIds => {
  const esProducts = pagesById.get(page)?.data ?? [];

  const productIds = Array.from(
    new Set(
      esProducts.flatMap((esProduct) =>
        saleItemIsProductColor(esProduct.item) ? [esProduct.item.productId] : [],
      ),
    ),
  );

  const catalogEntityIds = Array.from(
    new Set(
      esProducts.map((esProduct) =>
        saleItemIsKit(esProduct.item) ? esProduct.item.kitId : esProduct.item.productColorId,
      ),
    ),
  );

  return { productIds, catalogEntityIds };
};

const hasPageEntityIds = (ids: PageEntityIds): boolean =>
  ids.productIds.length > 0 || ids.catalogEntityIds.length > 0;

const entityIdOf = (esProduct: ESProductCatalogSearch): string =>
  saleItemIsKit(esProduct.item) ? esProduct.item.kitId : esProduct.item.productColorId;

export const useCatalogAccessSuggestions = (
  params: UseCatalogAccessSuggestionsParams,
): CatalogRowCardsAccess => {
  const { pagesById, anchorPage, customerIds, isOfflineMode, mainCustomerId } = params;

  const { isStore, isSeller, isAdminBrand } = useApplicationContext();

  const { selectedOrder, isShopping, orderProductsIds } = useOrderBookStore(
    useShallow((state) => ({
      selectedOrder: state.selectedOrder,
      isShopping: state.isShopping,
      orderProductsIds: state.orderProductsIds,
    })),
  );

  const normalizedCustomerIds = isStore ? [] : customerIds;
  const hasFetchData = isStore || isSeller || isAdminBrand || customerIds.length > 0;

  const behindIds = getPageEntityIds(pagesById, anchorPage - 1);
  const currentIds = getPageEntityIds(pagesById, anchorPage);
  const aheadIds = getPageEntityIds(pagesById, anchorPage + 1);
  const aheadNextIds = getPageEntityIds(pagesById, anchorPage + 2);

  const behindSuggestions = useSuggestedProducts(
    {
      customerIds: normalizedCustomerIds,
      productIds: behindIds.productIds,
      mainCustomerId,
      catalogEntityIds: behindIds.catalogEntityIds,
    },
    { enabled: hasFetchData && hasPageEntityIds(behindIds) },
  );

  const currentSuggestions = useSuggestedProducts(
    {
      customerIds: normalizedCustomerIds,
      productIds: currentIds.productIds,
      mainCustomerId,
      catalogEntityIds: currentIds.catalogEntityIds,
    },
    { enabled: hasFetchData && hasPageEntityIds(currentIds) },
  );

  const aheadSuggestions = useSuggestedProducts(
    {
      customerIds: normalizedCustomerIds,
      productIds: aheadIds.productIds,
      mainCustomerId,
      catalogEntityIds: aheadIds.catalogEntityIds,
    },
    { enabled: hasFetchData && hasPageEntityIds(aheadIds) },
  );

  const aheadNextSuggestions = useSuggestedProducts(
    {
      customerIds: normalizedCustomerIds,
      productIds: aheadNextIds.productIds,
      mainCustomerId,
      catalogEntityIds: aheadNextIds.catalogEntityIds,
    },
    { enabled: hasFetchData && hasPageEntityIds(aheadNextIds) },
  );

  const suggestionsResults = [
    behindSuggestions.data,
    currentSuggestions.data,
    aheadSuggestions.data,
    aheadNextSuggestions.data,
  ];

  const accessibleEntityIds = new Set(
    suggestionsResults.flatMap((result) =>
      (result?.validProductsToCustomer ?? []).map((validProduct) => validProduct.id),
    ),
  );

  const suggestedProductColorIds = new Set(
    suggestionsResults.flatMap((result) =>
      (result?.suggestedProducts ?? []).flatMap((suggestedProduct) =>
        suggestedProduct.suggestedProductsInfo.map((info) => info.productColorId),
      ),
    ),
  );

  const canAddToOrder = (esProduct: ESProductCatalogSearch): boolean =>
    isOfflineMode || isStore || isAdminBrand || accessibleEntityIds.has(entityIdOf(esProduct));

  const isSuggested = (esProduct: ESProductCatalogSearch): boolean => {
    if (saleItemIsKit(esProduct.item)) {
      return false;
    }

    if (selectedOrder?.status === StatusType.SUGGESTED) {
      return isShopping(esProduct.item.productColorId, orderProductsIds);
    }

    return suggestedProductColorIds.has(esProduct.item.productColorId);
  };

  return { canAddToOrder, isSuggested };
};
